import { Auth, Storage } from '~/amplify';
import { v4 as uuidv4 } from 'uuid';
import { isNil } from 'ramda';
import cleanedFilename from '../cleanedFilename';
import { assertNever } from '../assertNever';
import getUploadDataInput from './utils/getUploadDataInput';

export type ProtectionLevel = 'public' | 'private';

type CommonUploadType = {
  file: File;
  fileName?: string;
  fileId?: string;
  level?: ProtectionLevel;
};

type ImageType = CommonUploadType & {
  type: 'Image';
};

type PluginsEditorImageUploadType = CommonUploadType & {
  type: 'PluginsEditorImage';
  userId: string;
};

type FileType = CommonUploadType & {
  type: 'File';
  userId: string;
};

export type UploadFileType =
  | ImageType
  | PluginsEditorImageUploadType
  | FileType;

type S3FileUploadResult = {
  ContentType: string;
  ContentLength: number;
  key: string;
  Body: Blob;
  fileId: string;
  fileName: string;
  path: string;
};

type S3ImageUploadResult = {
  permanentUrl: string;
  filename: string;
  path: string;
  contentType: string;
  contentLength: number;
  identityId: string;
};

type UploadReturnType<T extends UploadFileType> = T extends FileType
  ? S3FileUploadResult
  : S3ImageUploadResult;

async function upload<T extends UploadFileType>(
  uploadArgs: T,
): Promise<UploadReturnType<T>> {
  const session = await Auth.fetchAuthSession();
  if (!session.identityId) {
    throw new Error('No identity ID available for upload');
  }
  const identityId = session.identityId;

  const { dataInput, processedFileName } = getUploadDataInput({
    uploadArgs,
    identityId,
  });

  const result = await Storage.uploadData(dataInput).result;
  const path = result.path;

  switch (uploadArgs.type) {
    case 'Image':
    case 'PluginsEditorImage': {
      const { url } = await Storage.getUrl({ path });
      const { contentType, size } = result;

      if (!contentType || isNil(size)) {
        throw Error(
          `${cleanedFilename(__filename)} | Should not occur | ContentType is undefined`,
        );
      }

      return {
        permanentUrl: url.toString(),
        filename: processedFileName,
        path,
        contentType,
        contentLength: size,
        identityId,
      } as UploadReturnType<T>;
    }

    case 'File': {
      const downloadResult = Storage.downloadData({ path });

      const { contentType, size } = await downloadResult.result;
      const body = await (await downloadResult.result).body.blob();

      if (!contentType || isNil(size)) {
        throw Error(
          `${cleanedFilename(__filename)} | Should not occur | ContentType is undefined`,
        );
      }

      return {
        ContentType: contentType,
        ContentLength: size,
        key: path,
        Body: body,
        fileId: uploadArgs.fileId || uuidv4(),
        fileName: processedFileName,
        path,
      } as UploadReturnType<T>;
    }

    default:
      return assertNever(uploadArgs);
  }
}

const uploadToS3Bucket = {
  image: (args: Omit<ImageType, 'type'>) => upload({ ...args, type: 'Image' }),
  pluginsEditorImage: (args: Omit<PluginsEditorImageUploadType, 'type'>) =>
    upload({ ...args, type: 'PluginsEditorImage' }),
  file: (args: Omit<FileType, 'type'>) => upload({ ...args, type: 'File' }),
};

export default uploadToS3Bucket;
